import { useGetPerformanceReportQuery } from "api/TenantSlug/Report/hooks";
import { MoneyCell } from "components/UI/Table/UI/MoneyCell/MoneyCell";
import dayjs from "dayjs";
import "dayjs/locale/cs";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { useUser } from "hooks/useUser";
import { PayoutResult, PerformanceReportDataSourceEnum, PerformanceReportViewEnum } from "types/enums";

import { COMPACT_DAYS } from "../shared/constants";
import { SearchableTable } from "../shared/SearchableTable";
import { renderDateSmart } from "../shared/utils/dateLocale";

type MoneyData = App.Data.MoneyData;
type Series = App.Http.Responses.Report.PerformanceReportSeriesResponse;

export const DailyOverview: React.FC = () => {
    const { isAdmin } = useUser();
    const { t: tG } = usePaldockTranslation("tables.general-columns");
    const { t: tR } = usePaldockTranslation("ui.report.columns");

    const start = dayjs()
        .subtract(COMPACT_DAYS - 1, "day")
        .startOf("day")
        .format("YYYY-MM-DD");
    const end = dayjs().endOf("day").format("YYYY-MM-DD");
    const userUtils = useUser();

    const q = useGetPerformanceReportQuery(
        {
            page: {
                number: 1,
                size: COMPACT_DAYS,
            },
            filter: {
                filter: {
                    datetime_at: { btw: `${start},${end}` },
                    payout_result: {
                        in: [PayoutResult.Approved],
                    },
                },
                sort: "-datetime_at",
            },
            view: isAdmin() ? PerformanceReportViewEnum.TenantAdmin : PerformanceReportViewEnum.AffiliatePartner,
            source: PerformanceReportDataSourceEnum.ReportPayoutPerformances,
        },
        {
            staleTime: 5000,
        }
    );

    const rows = q.data?.series ?? [];

    return (
        <SearchableTable<Series>
            rows={rows}
            loading={q.isLoading}
            rowKey={(r) => r.owner?.id || r.datetime_at || r.lead.toString()}
            searchKeys={[
                (r) => renderDateSmart(r.datetime_at),
                (r) => (r.datetime_at ? dayjs(r.datetime_at).format("D.M.YYYY") : ""),
                (r) => String(r.click ?? ""),
                (r) => String(r.lead ?? ""),
                (r) => String(r.count ?? ""),
                (r) => r.revenue?.formatted_amount ?? String(r.revenue?.amount ?? ""),
                (r) => r.cost?.formatted_amount ?? String(r.cost?.amount ?? ""),
                (r) => r.profit?.formatted_amount ?? String(r.profit?.amount ?? ""),
            ]}
            allowSearch={false}
            allowSuffix={false}
            columns={[
                {
                    title: tG("date"),
                    dataIndex: "datetime_at",
                    render: (v: string) => renderDateSmart(v),
                },
                { title: tR("clicks"), dataIndex: "click" },
                { title: tR("leads"), dataIndex: "lead" },
                { title: tR("transactions"), dataIndex: "count" },
                {
                    title: tR("revenue"),
                    dataIndex: "revenue",
                    render: (m: MoneyData) => <MoneyCell money={m} />,
                },
                ...(userUtils.isAdmin()
                    ? [
                          {
                              title: tR("cost"),
                              dataIndex: "cost",
                              render: (m: MoneyData) => <MoneyCell money={m} />,
                          },
                      ]
                    : []),
                ...(userUtils.isAdmin()
                    ? [
                          {
                              title: tR("profit"),
                              dataIndex: "profit",
                              render: (m: MoneyData) => <MoneyCell money={m} />,
                          },
                      ]
                    : []),
            ]}
            maxRows={COMPACT_DAYS}
            compact
        />
    );
};
